import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { activeTenantKey, resolveTenantDefaultPath, useTenant } from '@auw/tenant';
import { takeReturnPath } from '@/routes/authReturn';
import { hasAuthToken, setAuthTokens } from '@auw/api-client';
import { exchangeAuthCode, exchangeAuthCodeForDesktop } from '@auw/api-client';
import { useTranslation } from 'react-i18next';

/* ── Keyframes ── */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const BlankScreen = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

/* ── Error / Fallback ── */

const ErrorWrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const ErrorBox = styled.div`
  max-width: 420px;
  width: 100%;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  text-align: center;
  animation: ${fadeIn} 0.4s ease-out;
`;

const ErrorIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.errorBg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
`;

const ErrorTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ErrorDesc = styled.p`
  margin: 0 0 20px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

const RetryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 20px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    border-color: ${({ theme }) => theme.colors.borderInput};
  }
`;

/* ── Component ── */

interface Props {
  onAuthed: () => Promise<void> | void;
}

export const AuthCallbackPage: React.FC<Props> = ({ onAuthed }) => {
  const { t } = useTranslation('auth');
  const nav = useNavigate();
  const tenant = useTenant();
  const defaultPath = resolveTenantDefaultPath(tenant);
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const status = query.get('auth_status');
  const authCode = query.get('code');
  const teamInvite = query.get('teamInvite');
  const error = query.get('error');
  const isDesktopCallback = query.get('desktop') === '1';
  const startedCodeRef = useRef<string | null>(null);

  const [exchangeState, setExchangeState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [exchangeError, setExchangeError] = useState('');
  const [desktopDone, setDesktopDone] = useState(false);

  useEffect(() => {
    if (status !== 'success' || !authCode) return;
    if (startedCodeRef.current === authCode) return;
    startedCodeRef.current = authCode;

    const nextPath = teamInvite
      ? `${defaultPath}?teamInvite=${encodeURIComponent(teamInvite)}`
      : takeReturnPath() ?? defaultPath;
    const consumedCodeKey = activeTenantKey(`auth_code_consumed:${authCode}`);
    const getErrorMessage = (err: unknown) => (
      err instanceof Error && err.message.trim()
        ? err.message
        : '로그인 처리에 실패했습니다.'
    );
    const completeLogin = async () => {
      setExchangeState('loading');
      await Promise.resolve(onAuthed());
      setExchangeState('done');
      nav(nextPath, { replace: true });
    };
    const run = async () => {
      try {
        try {
          if (sessionStorage.getItem(consumedCodeKey) === '1') {
            if (!hasAuthToken()) {
              throw new Error('이미 처리된 로그인 코드입니다. 로그인 화면에서 다시 시도해주세요.');
            }
            if (isDesktopCallback) return;
            await completeLogin();
            return;
          }
        } catch (err) {
          if (err instanceof Error && err.message.includes('이미 처리된 로그인 코드입니다')) {
            throw err;
          }
        }

        if (isDesktopCallback) {
          setExchangeState('loading');
          const desktopResult = await exchangeAuthCodeForDesktop(authCode);
          try { sessionStorage.setItem(consumedCodeKey, '1'); } catch { /* no-op */ }

          const dlParams = new URLSearchParams();
          dlParams.set('access_token', desktopResult.access_token);
          if (desktopResult.refresh_token) {
            dlParams.set('refresh_token', desktopResult.refresh_token);
          }
          window.location.href = `askyourai://auth/callback?${dlParams.toString()}`;
          await new Promise(r => setTimeout(r, 1500));
          setDesktopDone(true);
          return;
        }

        setExchangeState('loading');
        const result = await exchangeAuthCode(authCode);
        try {
          sessionStorage.setItem(consumedCodeKey, '1');
        } catch {
          // no-op
        }

        setAuthTokens(result.access_token);
        await completeLogin();
      } catch (err) {
        startedCodeRef.current = null;
        setExchangeState('error');
        setExchangeError(getErrorMessage(err));
      }
    };
    void run();
  }, [status, authCode, teamInvite, onAuthed, nav, isDesktopCallback, defaultPath]);

  if (desktopDone) {
    return (
      <ErrorWrap>
        <ErrorBox>
          <ErrorIcon style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e' }}>✓</ErrorIcon>
          <ErrorTitle>{t('callback.desktopDoneTitle', '로그인 완료')}</ErrorTitle>
          <ErrorDesc>{t('callback.desktopDoneDesc', '데스크톱 앱으로 돌아가세요. 이 탭은 닫아도 됩니다.')}</ErrorDesc>
        </ErrorBox>
      </ErrorWrap>
    );
  }

  if (status === 'success' && authCode && (exchangeState === 'idle' || exchangeState === 'loading' || exchangeState === 'done')) {
    if (isDesktopCallback && !desktopDone) {
      return (
        <ErrorWrap>
          <ErrorBox>
            <ErrorIcon style={{ background: 'rgba(59,130,246,0.12)', color: '#3b82f6' }}>⟳</ErrorIcon>
            <ErrorTitle>{t('callback.processing', '로그인 처리 중...')}</ErrorTitle>
          </ErrorBox>
        </ErrorWrap>
      );
    }
    return <BlankScreen />;
  }

  const errorMessage = exchangeState === 'error'
    ? exchangeError
    : error || 'OAuth 처리 중 오류가 발생했습니다.';

  return (
    <ErrorWrap>
      <ErrorBox>
        <ErrorIcon>!</ErrorIcon>
        <ErrorTitle>로그인에 실패했습니다</ErrorTitle>
        <ErrorDesc>{errorMessage}</ErrorDesc>
        <RetryBtn onClick={() => nav(defaultPath)}>
          로그인 화면으로 돌아가기
        </RetryBtn>
      </ErrorBox>
    </ErrorWrap>
  );
};
